import { useState, useEffect, useCallback, useRef } from "react";

import {
  ProjectTime,
  ProjectTimeFromJSON,
} from "../bitmaker/schemas/Projects/ProjectTime";
import { ProjectTimeItem } from "../bitmaker/schemas/Projects/ProjectTimeItem";
import { BitmakerAPIClient } from "../services/BitmakerApp";
import { monthsRange } from "../utils";
import { useApiError } from "./useApiError";

const monthNames = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface FocusUser {
  user_login: string;
  hours: number;
}

type MonthData = {
  name: string;
  users: { user: string; hours: number }[];
};

export const useProjectTimeStats = (
  projectId: number,
  fromDate: Date,
  toDate: Date,
) => {
  const [stats, setStats] = useState<ProjectTime | null>(null);
  const [chartData, setChartData] = useState<any[]>([]);
  const [activities, setActivities] = useState<string[]>([]);
  const [focusInfo, setFocusInfo] = useState<FocusUser[] | null>(null);
  const [summaryTitle, setSummaryTitle] = useState("");
  const [averageTotalHours, setAverageTotalHours] = useState(0);

  const [loadingStats, setLoadingStats] = useState(false);
  const [fetchStatsError, setFetchStatsError] = useState<string | null>(null);

  const summaryRef = useRef<HTMLDivElement>(null);
  const isMountedRef = useRef(false);

  const { handleAPIError } = useApiError();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const buildChartData = useCallback(
    (projectTime: ProjectTime) => {
      const months = monthsRange(fromDate, toDate);
      const activityObjs: Record<string, ProjectTimeItem[]> = {};
      let totalHours = 0;
      const data: any[] = [];

      projectTime.report.forEach((timeItem) => {
        const name = timeItem.activity.name;
        if (!activityObjs[name]) activityObjs[name] = [];
        activityObjs[name].push(timeItem);
      });

      months.forEach((month) => {
        const monthObj: any = {
          name: `${monthNames[month.getUTCMonth()]} ${month.getUTCFullYear()}`,
          total: 0,
          users: [],
        };

        const userHoursMap: Record<string, number> = {};

        Object.keys(activityObjs).forEach((activity) => {
          monthObj[activity] = 0;

          activityObjs[activity].forEach((timeItem) => {
            if (
              timeItem.period.getUTCMonth() === month.getUTCMonth() &&
              timeItem.period.getUTCFullYear() === month.getUTCFullYear()
            ) {
              monthObj[activity] += timeItem.spent_time;

              if (timeItem.user_login) {
                userHoursMap[timeItem.user_login] =
                  (userHoursMap[timeItem.user_login] || 0) +
                  timeItem.spent_time;
              }
            }
          });

          monthObj.total += monthObj[activity];
        });

        monthObj.users = Object.entries(userHoursMap).map(([user, hours]) => ({
          user,
          hours,
        }));

        totalHours += monthObj.total;
        data.push(monthObj);
      });

      const avg = months.length ? totalHours / months.length : 0;

      return {
        data,
        activities: Object.keys(activityObjs).concat(["total"]),
        average: avg,
      };
    },
    [fromDate, toDate],
  );

  const fetchStats = useCallback(() => {
    if (!projectId || !isMountedRef.current) return;

    setLoadingStats(true);
    setFetchStatsError(null);

    BitmakerAPIClient.get(
      `/projects/${projectId}/time`,
      {
        from: fromDate.toISOString().slice(0, 10),
        to: toDate.toISOString().slice(0, 10),
      },
      (response: any) => {
        if (!isMountedRef.current) return;

        const { isError, message } = handleAPIError(response);

        if (isError) {
          console.error("Error fetching time stats:", message);
          setFetchStatsError("Error fetching project time stats.");
          setLoadingStats(false);
          return;
        }

        const parsed = ProjectTimeFromJSON(response.data);
        const { data, activities, average } = buildChartData(parsed);

        setStats(parsed);
        setChartData(data);
        setActivities(activities);
        setAverageTotalHours(average);
        setLoadingStats(false);
      },
    );
  }, [projectId, fromDate, toDate, handleAPIError, buildChartData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const onTooltipClick = useCallback((monthData: MonthData) => {
    if (!monthData) return;

    const monthYear = monthData.name;
    const monthUsers = monthData.users || [];

    const focus: FocusUser[] = monthUsers
      .map((userWithHours: { user: string; hours: number }) => ({
        user_login: userWithHours.user,
        hours: userWithHours.hours,
      }))
      .sort((a: FocusUser, b: FocusUser) => b.hours - a.hours);

    setFocusInfo(focus);
    setSummaryTitle(monthYear);

    setTimeout(() => {
      summaryRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 0);
  }, []);

  return {
    stats,
    chartData,
    activities,
    focusInfo,
    summaryTitle,
    averageTotalHours,
    summaryRef,
    loadingStats,
    fetchStatsError,
    onTooltipClick,
    refetchStats: fetchStats,
  };
};
